import type { MarketListing } from "../../domain/types.ts";
import type { CandidatePipelineResult } from "../marketDataProvider.ts";
import type { PublicMarketInvalidReason } from "../publicMarketNormalization.ts";

export const REALITY_RADAR_COLUMNS = [
  "Meno inzerenta",
  "Telefón",
  "Email",
  "Realitná kancelária",
  "Doména",
  "Odkaz na ponuku",
  "Typ predajcu",
  "Typ transakcie",
  "Kategória",
  "Cena",
  "Mena",
  "Typ ceny",
  "Cena (EUR)",
  "Cena za m² (EUR)",
  "Lokalita",
  "Štát",
  "Región",
  "Okres",
  "Obec",
  "Kód lokality",
  "PSČ",
  "Celková plocha (m²)",
  "Plocha pozemku (m²)",
  "Zastavaná plocha (m²)",
  "Počet izieb",
  "Stav nehnuteľnosti",
  "Inžinierske siete"
] as const;

export type RealityRadarColumn = (typeof REALITY_RADAR_COLUMNS)[number];
export type RealityRadarRawRow = Partial<Record<RealityRadarColumn, string | number | boolean | null>>
  & Record<string, string | number | boolean | null>;

export type RealityRadarListingRecord = {
  listing: MarketListing;
  municipalityId?: string;
  transactionType?: string;
  rawRows: RealityRadarRawRow[];
};

export type RealityRadarInvalidRecord = {
  rowNumber: number;
  primaryReason: PublicMarketInvalidReason;
  reasons: PublicMarketInvalidReason[];
  rawRow: RealityRadarRawRow;
};

export type RealityRadarInvalidSample = {
  rowNumber: number;
  municipality?: string;
  propertyType?: string;
  rooms?: string;
  area?: string;
  price?: string;
  sourceUrl?: string;
};

export type RealityRadarImportSnapshot = {
  id: string;
  connectorId: "reality-radar-xlsx-v1";
  fileName: string;
  fileHash: string;
  capturedAt: string;
  columns: string[];
  rowsLoaded: number;
  invalidRows: number;
  invalidReasonCounts: Partial<Record<PublicMarketInvalidReason, number>>;
  invalidPrimaryReasonCounts: Partial<Record<PublicMarketInvalidReason, number>>;
  invalidSamplesByReason: Partial<Record<PublicMarketInvalidReason, RealityRadarInvalidSample[]>>;
  invalidRecords: RealityRadarInvalidRecord[];
  normalizedRows: number;
  duplicateRowsRemoved: number;
  duplicatesMerged: number;
  records: RealityRadarListingRecord[];
};

export type RealityRadarImportSummary = {
  snapshotId: string;
  capturedAt: string;
  fileName: string;
  rowsLoaded: number;
  invalidRows: number;
  invalidReasonCounts: Partial<Record<PublicMarketInvalidReason, number>>;
  invalidPrimaryReasonCounts: Partial<Record<PublicMarketInvalidReason, number>>;
  invalidSamplesByReason: Partial<Record<PublicMarketInvalidReason, RealityRadarInvalidSample[]>>;
  duplicateRowsRemoved: number;
  duplicatesMerged: number;
  normalizedListings: number;
  uniqueListings: number;
};

export type RealityRadarImportPipelineResult = RealityRadarImportSummary & {
  relevantCandidates: number;
  topCandidates: number;
  excludedCandidates: number;
  ineligibleCandidates: number;
  invalidCandidates: number;
  rankedOutCandidates: number;
  pipeline: CandidatePipelineResult;
};
